import type { Dispatch, FC, SetStateAction } from "react"
import { createContext, useContext, useEffect, useMemo, useState } from "react"

import {
  Box,
  ButtonBase,
  FormControl,
  InputBase,
  NativeSelect,
  Stack,
  Typography,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import L from "leaflet"
import { Marker, Popup, useMap } from "react-leaflet"

import { Map as LeafletMap } from "@ggs/ui/local"
import { Container, Link, ResponsiveImage, ResponsiveImageType, SvgIcon } from ".."

type MarkerData = {
  label: string
  description: string
  type: string
  lat: number
  lng: number
  uri: string
  image: ResponsiveImageType
}

type MapProps = {
  items: MarkerData[]
  showFilters?: boolean
  country?: string
  initialFilter?: string
}

type MapContextType = {
  active: [number, Dispatch<SetStateAction<number>>]
}

const MapContext = createContext<MapContextType>({
  active: [-1, () => {}],
})

const Map: FC<MapProps> = ({ items = [], showFilters = false, country, initialFilter = null }) => {
  const [map, setMap] = useState<L.Map | null>(null)
  const [active, setActive] = useState(-1)
  const [activeFilter, setActiveFilter] = useState<string | null>(initialFilter)
  const [locations, setLocations] = useState(items)
  const center = useMemo(
    () => L.latLngBounds(locations.map((item) => L.latLng(item.lat, item.lng))).getCenter(),
    [locations]
  )

  useEffect(() => {
    if (activeFilter && items.some((item) => item.type === activeFilter)) {
      setLocations(items.filter((item) => item.type === activeFilter))
    } else {
      setLocations(items)
    }
  }, [activeFilter])

  useEffect(() => {
    const bounds = L.latLngBounds(locations.map((item) => L.latLng(item.lat, item.lng)))
    if (map && bounds.isValid()) {
      // Define the padding to add space on the right side for the sidebar.
      // Calculate an appropriate maxZoom dynamically.
      // Disable scroll wheel zoom only on mobile devices
      if (/Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        map.scrollWheelZoom.disable()
      }
      const maxZoom = bounds.getNorthEast().distanceTo(bounds.getSouthWest()) > 5000 ? 4 : 6
      map.fitBounds(bounds, {
        paddingTopLeft: [55, 90],
        paddingBottomRight: [310, 150],
        maxZoom: maxZoom,
      })
    }
  }, [locations, map])

  map?.zoomControl.setPosition("bottomleft")

  return (
    <Container
      borderTop={(theme) => ({ xs: `1px solid ${theme.palette["neutral.200"].main}`, lg: 0 })}
    >
      <MapContext.Provider value={{ active: [active, setActive] }}>
        {showFilters && (
          <MapFilters
            items={items}
            activeFilter={activeFilter}
            setActiveFilter={setActiveFilter}
            country={country}
          />
        )}
        <Stack
          direction={{ lg: "row-reverse" }}
          position="relative"
          sx={{ isolation: "isolate", aspectRatio: { xs: "7/9", md: "4/3", lg: "1520/700" } }}
        >
          {map && <MapSidebar map={map} markers={locations} />}
          <Box
            flex={{ xs: "1 0 100%", lg: "1 1 auto" }}
            borderRadius={(theme) => ({ xs: 1.5, lg: theme.spacing(1.5, 0, 0, 1.5) })}
            position="relative"
            overflow="hidden"
            sx={{
              ".leaflet-popup": {
                mb: 10,
              },
              ".leaflet-popup-content-wrapper": {
                boxShadow: (theme) =>
                  `2px 2px 20px ${alpha(theme.palette["neutral.900"].main, 0.08)}`,
                borderRadius: 1.5,
                minHeight: { xs: 80, lg: 300 },
                minWidth: { xs: 190, lg: "unset" },
                maxWidth: { xs: 210, lg: "unset" },
                overflow: "hidden",
              },

              ".leaflet-popup-content": {
                margin: 0,
              },

              ".leaflet-popup-tip-container": {
                display: "none",
              },

              ".leaflet-marker-icon": {
                bgcolor: "transparent",
                border: "none",
              },

              ".leaflet-control-zoom": {
                border: "0!important",
                display: "flex",
                flexDirection: "column",

                rowGap: 1.5,
              },

              ".leaflet-control-zoom-in, .leaflet-control-zoom-out": {
                borderRadius: "9999px!important",
                border: "0!important",
                boxShadow: "none!important",
                padding: "0!important",
                fontWeight: "400!important",
                fontSize: "16px!important",
                lineHeight: "16px!important",
                width: "30px!important",
                height: "30px!important",
                position: "relative",

                span: {
                  display: "none",
                },
              },

              ".leaflet-control-zoom-in::before": {
                background:
                  "url(\"data:image/svg+xml,%3Csvg width='11' height='10' viewBox='0 0 11 10' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M6 0V10H5V0H6Z' fill='%23464446'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M10.5 5.5H0.5V4.5H10.5V5.5Z' fill='%23464446'/%3E%3C/svg%3E%0A\")",
                content: "''",
                display: "block",
                width: 11,
                height: 10,
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              },

              ".leaflet-control-zoom-out::before": {
                background:
                  "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 11 2' fill='%23464446' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M10.5 1.5H0.5V0.5H10.5V1.5Z' /%3E%3C/svg%3E%0A\")",
                content: "''",
                width: 11,
                height: 2,
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              },
            }}
          >
            <LeafletMap setMap={setMap} center={center}>
              {locations.map((item, i) => (
                <MapMarker key={i} index={i} {...item} />
              ))}
            </LeafletMap>
          </Box>
        </Stack>
      </MapContext.Provider>
    </Container>
  )
}
export default Map

type FilterProps = {
  items: MarkerData[]
  activeFilter: string | null
  setActiveFilter: Dispatch<SetStateAction<string | null>>
  country?: string
}

const MapFilters: FC<FilterProps> = ({ items, activeFilter, setActiveFilter, country }) => {
  const {
    active: [_, setActive],
  } = useContext(MapContext)
  const categories = useMemo(() => {
    const filterCategories = new Set<string>()
    items.forEach((item) => {
      if (item.type) filterCategories.add(item.type)
    })
    return filterCategories
  }, [items])

  if (categories.size === 0) {
    return null
  }

  return (
    <>
      <FormControl
        fullWidth
        sx={(theme) => ({
          display: { xs: "inline-flex", lg: "none" },
          bgcolor: theme.palette["neutral.100"].main,
          boxShadow: `0px 2px 10px 0px ${alpha(theme.palette["neutral.900"].main, 0.08)}`,
          borderRadius: 1,
          mt: 10,
          mb: 3,
        })}
      >
        <SvgIcon
          variant="sort"
          size={16}
          sx={{
            position: "absolute",
            top: "50%",
            left: 20,
            transform: "translateY(-50%)",
            zIndex: 1,
          }}
        />
        <NativeSelect
          onChange={(e) => {
            setActive(-1) // if there's an active marker, reset it
            setActiveFilter(e.target.value || null)
          }}
          value={activeFilter}
          input={
            <InputBase
              placeholder="Filter By:"
              sx={(theme) => ({
                "height": 54,
                "pr": 5,
                "pl": 12,

                "& .MuiInputBase-input": {
                  ...theme.typography["link.sm"],
                },
              })}
            />
          }
          inputProps={{}}
          IconComponent={SelectDropdownIcon}
        >
          <option value={""}>Show All</option>
          {[...categories].map((category, i) => (
            <option key={i} value={category}>
              {category}
            </option>
          ))}
        </NativeSelect>
      </FormControl>

      <Stack
        height={100}
        direction="row"
        alignItems="center"
        borderTop={(theme) => ({ xs: 0, lg: `1px solid ${theme.palette["neutral.200"].main}` })}
        px={{ lg: 4.5 }}
        columnGap={15}
        mt={{ xs: -3, lg: 0 }}
        sx={{
          display: { xs: "none", lg: "flex" },
        }}
      >
        <Typography variant="link.sm">Filter By:</Typography>
        <MapFilterBtn
          category="Show All"
          active={activeFilter === null}
          onClick={() => {
            setActive(-1) // if there's an active marker, reset it
            setActiveFilter(null)
          }}
        />
        {[...categories].map((category, i) => (
          <MapFilterBtn
            key={i}
            category={category}
            active={activeFilter === category}
            country={country}
            onClick={() => {
              setActive(-1) // if there's an active marker, reset it
              setActiveFilter(category)
            }}
          />
        ))}
      </Stack>
    </>
  )
}

const SelectDropdownIcon = () => (
  <SvgIcon variant="bullet" size={8} sx={{ transform: "rotate(90deg)" }} />
)

type FilterBtnProps = {
  category: string
  active: boolean
  onClick: () => void
  country?: string
}

const MapFilterBtn: FC<FilterBtnProps> = ({ category, active, onClick, country }) => {
  return (
    <Link
      // @ts-ignore
      component="button"
      variant="link.sm"
      onClick={onClick}
      sx={{
        "py": 1.5,
        "backgroundSize": active ? "100% 1px" : "0 1px",
        "textTransform": "capitalize",

        "&:hover, &:active": {
          backgroundSize: "100% 1px",
        },
      }}
    >
      {country === "Canada" && category === "State" ? "Province" : category}
    </Link>
  )
}

const MapMarker: FC<MarkerData & { index: number }> = ({
  lat,
  lng,
  label,
  description,
  image,
  uri,
  index,
}) => {
  const map = useMap()
  const {
    active: [active, setActive],
  } = useContext(MapContext)

  const markerIcon = useMemo(() => {
    if (typeof window !== "undefined") {
      return new L.Icon({
        iconUrl: "/images/map-marker.svg",
        iconSize: [40, 40],
        shadowSize: [0, 0],
      })
    }
  }, [])

  const activeIcon = useMemo(() => {
    if (typeof window !== "undefined") {
      return new L.Icon({
        iconUrl: "/images/map-marker-active.svg",
        iconSize: [48, 48],
        shadowSize: [0, 0],
      })
    }
  }, [])

  return (
    <Marker
      position={[lat, lng]}
      icon={active === index ? activeIcon : markerIcon}
      eventHandlers={{
        popupopen: (e) => {
          const px = map.project(e.target._popup._latlng)
          px.y -= e.target._popup._container.clientHeight / 2
          map.flyTo(map.unproject(px))
          setActive(index)
        },
        popupclose: () => {
          setActive(-1)
        },
      }}
    >
      <Popup closeButton={false} offset={[0, 0]}>
        <Stack
          direction={{ xs: "row", lg: "column" }}
          columnGap={3}
          py={1}
          pb={{ xs: 1, lg: 0 }}
          px={1}
        >
          <Box
            position="relative"
            sx={{ aspectRatio: { xs: "1", lg: "29/18" }, width: { xs: 72, md: 293 } }}
            height={{ xs: 72, lg: 180 }}
          >
            <ResponsiveImage
              image={image}
              width={{ xs: "72px", lg: "100%" }}
              height="100%"
              borderRadius={1}
              sx={{ display: "block", objectFit: "cover" }}
            />
            <Link type="overlay" href={uri} />
          </Box>
          <Stack py={6} px={5} spacing={3} display={{ xs: "none", lg: "block" }} minHeight={149}>
            <Link
              type="base"
              variant="body.lg"
              href={uri}
              sx={{
                color: (theme) => `${theme.palette["neutral.700"].main}!important`,
              }}
            >
              {label}
            </Link>
            <Typography
              variant="body.sm"
              component="div"
              dangerouslySetInnerHTML={{ __html: description }}
              sx={{
                "overflow": "hidden",
                "display": "-webkit-box",
                "WebkitBoxOrient": "vertical",
                "WebkitLineClamp": 3,

                "& > *": {
                  display: "inline",
                },
              }}
            />
          </Stack>
          <Stack
            direction={{ xs: "column", lg: "row" }}
            alignItems={{ xs: "flex-start", lg: "center" }}
            justifyContent={{ xs: "center", lg: "space-between" }}
            sx={(theme) => ({
              ...theme.typography["link.xs"],
              "color": `${theme.palette["neutral.700"].main}!important`,
              "rowGap": 3,
              "py": { xs: 0, lg: 3.5 },
              "px": { xs: 0, lg: 5 },
              "pr": { xs: 2, lg: 5 },
              "borderTop": { lg: `1px solid ${theme.palette["neutral.200"].main}` },
              "position": "relative",
              "mt": { lg: "auto" },
              "transition": theme.transitions.create(["color"]),

              "&:hover": {
                color: `${theme.palette["neutral.800"].main}!important`,

                svg: {
                  transform: "rotate(180deg) translateX(-6px)",
                },
              },
            })}
          >
            <Link type="overlay" href={uri} aria-label={`Discover ${label}`} sx={{ zIndex: 201 }} />
            <span>Discover {label}</span>
            <SvgIcon
              variant="arrow"
              size={14}
              sx={{
                transform: "rotate(180deg)",
                display: { xs: "none", lg: "block" },
                transition: (theme) => theme.transitions.create(["transform"]),
              }}
            />
            <SvgIcon
              variant="arrowOutward"
              strokeMode
              size={10}
              sx={{ display: { xs: "block", lg: "none" } }}
            />
          </Stack>
        </Stack>
      </Popup>
    </Marker>
  )
}

const MapSidebar: FC<{ map: L.Map; markers: MarkerData[] }> = ({ map, markers }) => {
  const {
    active: [activeIndex],
  } = useContext(MapContext)

  const openMapMarker = ({ lat, lng }: { lat: number; lng: number }) => {
    map.eachLayer((layer) => {
      if (layer instanceof L.Marker) {
        if (layer.getLatLng().equals([lat, lng])) {
          layer.openPopup(layer.getLatLng())
          return
        }
      }
    })
  }

  useEffect(() => {
    if (map) {
      // Disable scroll wheel zoom only on mobile devices
      if (/Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        map.scrollWheelZoom.disable()
      }
    }
  }, [map])

  return (
    <Box
      bgcolor={(theme) => theme.palette["neutral.100"].main}
      border={(theme) => ({ lg: `1px solid ${theme.palette["neutral.200"].main}` })}
      borderRadius={(theme) => theme.spacing(0, 1.5, 1.5, 0)}
      px={3}
      pb={{ xs: 10, lg: 0 }}
      height={{ xs: "auto!important", lg: "100%" }}
      width={{ lg: 276 }}
      sx={{
        "overflowY": "scroll",
        "textAlign": { xs: "center", lg: "left" },
        "-webkit-overflow-scrolling": "touch",
      }}
    >
      <Box minHeight={70} px={7} pt={5} pb={4} display="flex" alignItems="center">
        <Typography variant="link.sm" sx={{ width: "100%" }}>
          Where are you going?
        </Typography>
      </Box>
      <Box
        display={{ xs: "flex", lg: "block" }}
        columnGap={3}
        sx={{
          "cursor": { xs: "ew-resize", lg: "unset" },
          "flexDirection": { xs: "row", lg: "unset" },
          "overflowX": "auto",
          "msOverflowStyle": "none",
          "scrollbarWidth": "none",

          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        {markers.map(({ lat, lng, label }, i) => (
          <MapSidebarBtn
            key={i}
            active={activeIndex === i}
            label={label}
            onClick={() => openMapMarker({ lat, lng })}
          />
        ))}
      </Box>
    </Box>
  )
}

const MapSidebarBtn: FC<{
  active: boolean
  label: string
  onClick: () => void
}> = ({ label, onClick, active = false }) => {
  const {
    active: [activeIndex],
  } = useContext(MapContext)

  return (
    <ButtonBase
      disableRipple
      onClick={onClick}
      sx={(theme) => ({
        ...theme.typography["body.md"],
        "bgcolor": theme.palette[active ? "secondary.200" : "neutral.100"].main,
        "borderColor": {
          xs: theme.palette[active ? "secondary.300" : "neutral.200"].main,
          lg: active ? "transparent" : `${theme.palette["neutral.200"].main} transparent`,
        },
        "color": theme.palette[active ? "secondary.800" : "neutral.700"].main,
        "borderRadius": { xs: 999, lg: "0" },
        "borderStyle": "solid",
        "borderWidth": { xs: "1px", lg: "1px 0 0 0" },
        "display": { xs: "inline-block", lg: "flex" },
        "fontWeight": active ? 500 : 400,
        "flexDirection": { lg: "row" },
        "alignItems": { lg: "center" },
        "justifyContent": { lg: "space-between" },
        "flex": { xs: "1 0 auto", lg: "auto" },
        "px": { xs: 6, lg: 7 },
        "py": { xs: 2, lg: 4 },
        "textAlign": { xs: "center", lg: "left" },
        "width": { xs: "auto", lg: "100%" },
        "transition": theme.transitions.create(
          ["border-color", "background-color", "color", "border-radius"],
          {
            duration: theme.transitions.duration.standard * 1.5,
          }
        ),

        "&:hover, &:focus-visible": {
          "bgcolor": theme.palette["secondary.200"].main,
          "borderColor": {
            xs: theme.palette["secondary.300"].main,
            lg: `${theme.palette["neutral.100"].main} transparent`,
          },
          "color": theme.palette["secondary.800"].main,
          "borderRadius": { xs: 99, lg: theme.spacing(1) },

          "& + *": {
            borderTopColor: "transparent",
          },
        },

        "&:not(:first-of-type)": {
          mt: { lg: "-1px" },
        },
      })}
    >
      {label}
      {active && (
        <SvgIcon variant="location" size={12} sx={{ display: { xs: "none", lg: "block" } }} />
      )}
    </ButtonBase>
  )
}
